import { useState } from 'react'
import { Bug, Plus, Loader2, ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { submitBugReport, fetchResolvedBugReports, deleteBugReport } from '../store/bugReportsSlice'
import type { BugReport } from '../store/bugReportsSlice'
import { isEmailAppEditor } from '../store/editorsSlice'
import './BugReportList.css'

interface BugReportListProps {
  appId: string
}

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  fixing: 'Fixing',
  fixed: 'Fixed',
  resolved: 'Resolved',
  error: 'Error',
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function BugReportItem({
  bug,
  canDelete,
  onDelete,
  deleteError,
}: {
  bug: BugReport
  canDelete: boolean
  onDelete: (bug: BugReport) => void
  deleteError?: string
}) {
  return (
    <div className="bug-report-item" data-testid="bug-report-item">
      <div className="bug-report-item__row">
        <span className={`bug-report-item__status bug-report-item__status--${bug.status}`} data-testid="bug-report-status">
          {STATUS_LABELS[bug.status] || bug.status}
        </span>
        <span className="bug-report-item__date">{formatDate(bug.created_at)}</span>
        <span className="bug-report-item__description">{bug.description}</span>
        {canDelete && bug.status !== 'fixing' && (
          <button
            className="bug-report-item__delete-btn"
            onClick={() => onDelete(bug)}
            aria-label="Delete bug report"
            data-testid="delete-bug-report-btn"
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>
      {deleteError && (
        <p className="bug-report-item__delete-error" role="alert" data-testid="delete-bug-report-error">
          {deleteError}
        </p>
      )}
    </div>
  )
}

function BugReportList({ appId }: BugReportListProps) {
  const dispatch = useAppDispatch()
  const { items, loading, submitting, resolvedItems, resolvedCount, resolvedHasMore, resolvedLoading, resolvedCursor } = useAppSelector((state) => state.bugReports)
  const authUser = useAppSelector((state) => state.auth.user)
  const editors = useAppSelector((state) => state.editors.items)
  const isSignedIn = !!authUser
  // The webhook accepts the App page's session only for editors.
  const isEditor = !!authUser && isEmailAppEditor(authUser.email, editors)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [description, setDescription] = useState('')
  const [resolvedExpanded, setResolvedExpanded] = useState(false)
  const [deleteErrors, setDeleteErrors] = useState<Record<string, string>>({})

  const activeBugs = items
  const resolvedBugs = resolvedItems

  const handleDelete = (bug: BugReport) => {
    const key = String(bug.id)
    setDeleteErrors((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    dispatch(deleteBugReport({ appId, id: key }))
      .unwrap()
      .catch((err: Error) => {
        setDeleteErrors((prev) => ({ ...prev, [key]: err.message || 'Failed to delete bug report' }))
      })
  }

  const handleSubmit = () => {
    if (!description.trim()) return
    setSubmitError(null)
    dispatch(submitBugReport({ appId, description: description.trim() }))
      .unwrap()
      .catch((err: Error) => setSubmitError(err.message || 'Failed to submit bug report'))
    setDescription('')
    setFormOpen(false)
  }

  const handleCancel = () => {
    setFormOpen(false)
    setDescription('')
  }

  return (
    <section className="bug-report-list" data-testid="bug-report-list" aria-labelledby="bug-report-list-heading">
      <div className="bug-report-list__header">
        <h2 className="bug-report-list__title" id="bug-report-list-heading">
          <Bug size={20} aria-hidden="true" />
          Bug Reports
        </h2>
        <div className="bug-report-list__actions">
          <button
            className="bug-report-list__report-btn"
            onClick={() => setFormOpen(true)}
            disabled={formOpen || !isEditor}
            title={isEditor ? undefined : 'Sign in as an editor of this app to report bugs'}
            data-testid="report-bug-btn"
          >
            <Plus size={16} aria-hidden="true" />
            Report Bug
          </button>
        </div>
      </div>
      {submitError && (
        <p className="bug-report-item__delete-error" role="alert" data-testid="bug-report-submit-error">
          {submitError}
        </p>
      )}
      {formOpen && (
        <div className="bug-report-list__form" data-testid="bug-report-form">
          <textarea
            className="bug-report-list__textarea"
            placeholder="Describe the bug..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="bug-report-textarea"
            rows={4}
          />
          <div className="bug-report-list__form-actions">
            <button
              className="bug-report-list__cancel-btn"
              onClick={handleCancel}
              data-testid="bug-report-cancel-btn"
            >
              Cancel
            </button>
            <button
              className="bug-report-list__submit-btn"
              onClick={handleSubmit}
              disabled={!description.trim() || submitting}
              data-testid="bug-report-submit-btn"
            >
              {submitting ? <Loader2 size={14} className="bug-report-list__spinner" aria-hidden="true" /> : null}
              Submit Bug Report
            </button>
          </div>
        </div>
      )}
      <div className="bug-report-list__items">
        {loading && items.length === 0 && resolvedItems.length === 0 && (
          <div className="bug-report-list__empty" role="status">Loading bug reports...</div>
        )}
        {!loading && items.length === 0 && resolvedCount === 0 && !formOpen && (
          <div className="bug-report-list__empty">No bug reports yet</div>
        )}
        {activeBugs.map((bug) => (
          <BugReportItem
            key={bug.id}
            bug={bug}
            canDelete={isSignedIn}
            onDelete={handleDelete}
            deleteError={deleteErrors[String(bug.id)]}
          />
        ))}
        {resolvedCount > 0 && (
          <div className="bug-report-list__resolved-section" data-testid="resolved-bugs-section">
            <button
              className="bug-report-list__resolved-header"
              onClick={() => setResolvedExpanded(!resolvedExpanded)}
              aria-expanded={resolvedExpanded}
              data-testid="resolved-bugs-toggle"
            >
              {resolvedExpanded ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
              <span className="bug-report-list__resolved-label">Resolved Bugs</span>
              <span className="bug-report-list__resolved-count">{resolvedCount}</span>
            </button>
            {resolvedExpanded && (
              <>
                {resolvedBugs.map((bug) => (
                  <BugReportItem
                    key={bug.id}
                    bug={bug}
                    canDelete={isSignedIn}
                    onDelete={handleDelete}
                    deleteError={deleteErrors[String(bug.id)]}
                  />
                ))}
                {resolvedHasMore && (
                  <button
                    className="bug-report-list__load-more"
                    onClick={() => dispatch(fetchResolvedBugReports({ appId, cursor: resolvedCursor }))}
                    disabled={resolvedLoading}
                    data-testid="load-more-resolved"
                  >
                    {resolvedLoading ? <Loader2 size={14} className="bug-report-list__spinner" aria-hidden="true" /> : null}
                    {resolvedLoading ? 'Loading...' : 'Load More'}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export default BugReportList
